/**
 * Chat API Service
 * Handles backend API calls for different thinking modes
 */

import { CHAT_API_ENDPOINTS, CHAT_CONFIG, MODE_CONFIG } from './chatConfig';
import { BackendRequestBody, ThinkingMode } from './chatTypes';

/**
 * Parameters for sending a message to the backend
 */
export interface SendMessageApiParams {
  inputText: string;
  isFirstMessage: boolean;
  currentConversationId?: string;
  id: string;
  thinkingMode: ThinkingMode;
  extra_parameters?: { [key: string]: any; fraktal_complexity?: string };
  attachments?: import('../../../models/Message').MessageAttachment[];
}

/**
 * Builds request body for Prompt Graph microservice
 */
const buildPromptGraphRequestBody = (params: SendMessageApiParams): any => {
  const { inputText, id, currentConversationId, extra_parameters, attachments } = params;
  const modeConfig = MODE_CONFIG['prompt-graph'];
  
  // Ensure we have all required parameters, with extra_parameters taking precedence
  const requestBody: any = {
    project_id: extra_parameters?.project_id || modeConfig.requestParams?.project_id,
    initial_inputs: {
      input_text: inputText
    },
    // Add conversation ID as request_id so backend can use it for Firebase path
    request_id: currentConversationId || id
  };

  // Include attachments if present (documents and URLs)
  // Send only IDs for documents and URLs for URL attachments (minimal tracking data)
  if (attachments && attachments.length > 0) {
    requestBody.attachments = attachments.map(att => ({
      type: att.type,
      ...(att.type === 'document' ? { id: att.id } : { url: att.url })
    }));
  }

  return requestBody;
};

/**
 * Builds request body for Fast Thinking microservice
 */
const buildFastThinkingRequestBody = (params: SendMessageApiParams): any => {
  const { inputText, currentConversationId, id, attachments } = params;
  const modeConfig = MODE_CONFIG['fast-thinking'];
  
  const requestBody: any = {
    query: inputText,
    execution_id: currentConversationId || id,
    request_id: id,
    web_search_enabled: modeConfig.requestParams?.web_search_enabled,
  };

  // Include attachments if present (documents and URLs)
  // Send only IDs for documents and URLs for URL attachments (minimal tracking data)
  if (attachments && attachments.length > 0) {
    requestBody.attachments = attachments.map(att => ({
      type: att.type,
      ...(att.type === 'document' ? { id: att.id } : { url: att.url })
    }));
  }

  return requestBody;
};

/**
 * Builds request body for legacy endpoints (reflection, fraktal)
 */
const buildLegacyRequestBody = (params: SendMessageApiParams): BackendRequestBody => {
  const { inputText, isFirstMessage, currentConversationId, id, thinkingMode, extra_parameters } = params;
  const modeConfig = MODE_CONFIG[thinkingMode];
  
  const requestBody: BackendRequestBody = {
    // Use 'input_text' for reflection modes, 'text' for fraktal
    [modeConfig.conversationType === 'reflection' ? 'input_text' : 'text']: inputText,
    parent_id: !isFirstMessage ? currentConversationId : undefined,
    ...CHAT_CONFIG.DEFAULT_REQUEST_PARAMS,
    id: id,
    conversation_type: modeConfig.conversationType,
    // Add mode-specific request parameters
    ...modeConfig.requestParams,
    // Add extra parameters if provided
    ...(extra_parameters && { extra_parameters }),
  };

  return requestBody;
};

/**
 * Constructs the request body for different thinking modes using MODE_CONFIG
 */
export const buildRequestBody = (params: SendMessageApiParams): any => {
  const { thinkingMode, isFirstMessage } = params;

  // Handle prompt-graph mode with context-aware routing
  if (thinkingMode === 'prompt-graph') {
    if (isFirstMessage) {
      return buildPromptGraphRequestBody(params);
    } else {
      // Route to fast-thinking for follow-up messages in prompt-graph conversations
      return buildFastThinkingRequestBody(params);
    }
  }

  // Handle fast-thinking mode (now using microservice)
  if (thinkingMode === 'fast-thinking') {
    return buildFastThinkingRequestBody(params);
  }

  // Handle legacy modes (reflection, fraktal)
  return buildLegacyRequestBody(params);
};

/**
 * Selects the appropriate API endpoint based on thinking mode and context
 */
export const selectEndpoint = (thinkingMode: ThinkingMode, isFirstMessage = false): string => {
  // Handle prompt-graph mode with context-aware routing
  if (thinkingMode === 'prompt-graph') {
    if (isFirstMessage) {
      return MODE_CONFIG['prompt-graph'].endpoint; // Prompt Graph microservice
    } else {
      return MODE_CONFIG['fast-thinking'].endpoint; // Fast Thinking microservice for follow-ups
    }
  }

  // Standard endpoint selection for other modes
  return MODE_CONFIG[thinkingMode].endpoint;
};

/**
 * Build a FormData body for multipart requests (new conversation with file attachments).
 * Files are sent as form fields alongside the JSON fields.
 */
const buildMultipartBody = (
  requestBody: any,
  files: File[]
): FormData => {
  const formData = new FormData();

  // Add JSON fields as individual form fields
  for (const [key, value] of Object.entries(requestBody)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  }

  // Add files
  files.forEach(file => {
    formData.append('files', file, file.name);
  });

  return formData;
};

/**
 * Sends a message to the backend using thinking mode endpoints.
 *
 * When `files` are provided (new conversation with documents), the request is
 * sent as multipart/form-data so the backend can receive the files alongside
 * the JSON fields.
 */
export const sendMessageToBackend = async (
  params: SendMessageApiParams,
  token: string,
  files?: File[]
): Promise<Response> => {
  const requestBody = buildRequestBody(params);
  const endpoint = selectEndpoint(params.thinkingMode, params.isFirstMessage);

  // Use multipart when files are present (new conversation with documents)
  if (files && files.length > 0 && params.isFirstMessage) {
    const formData = buildMultipartBody(requestBody, files);
    console.log('[API] Sending multipart to backend with', files.length, 'file(s)');

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // Do NOT set Content-Type — browser sets it with boundary for multipart
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorData}`);
    }

    return response;
  }

  // Standard JSON request
  const bodyString = JSON.stringify(requestBody);
  console.log('[API] Sending to backend:', JSON.parse(bodyString));

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: bodyString,
  });

  if (!response.ok) {
    const errorData = await response.text();
    throw new Error(`HTTP error! status: ${response.status}, message: ${errorData}`);
  }

  return response;
};